import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createPollEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { getLocalParticipant, getParticipantById } from '../../base/participants/functions';
import { editPoll, registerVote, setVoteChanging, updatePoll } from '../actions';
import { COMMAND_ADD_OPTION_POLL, COMMAND_ANSWER_POLL, COMMAND_DELETE_POLL, COMMAND_END_POLL, COMMAND_START_POLLS } from '../constants';
import { getPoll } from '../functions';
import { isLocalParticipantCanPoll } from '../../poll-moderation/functions';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../../notifications';
/**
 * Higher Order Component taking in a concrete PollAnswer component and
 * augmenting it with state/behavior common to both web and native implementations.
 *
 * @param {React.AbstractComponent} Component - The concrete component.
 * @returns {React.AbstractComponent}
 */
const AbstractPollAnswer = (Component) => (props) => {
    const { pollId, setCreateMode, disabled } = props;
    const { conference } = useSelector((state) => state['features/base/conference']);
    const poll = useSelector(getPoll(pollId));
    const localParticipant = useSelector((state) => getLocalParticipant(state));
    ;
    const [optionValue, setOptionValue] = useState('');
    const [validateMaxLength, setValidateMaxLength] = useState(false);
    const canCreatePoll = useSelector(isLocalParticipantCanPoll);
    const { answers, lastVote, question, senderId, senderUserId, senderName } = poll;
    const [checkBoxStates, setCheckBoxState] = useState(() => {
        if (lastVote !== null) {
            return [...lastVote];
        }
        return new Array(answers.length).fill(false);
    });
    // const participantName = useBoundSelector(getParticipantDisplayName, senderId);
    const participantName = useSelector((state) => getParticipantById(state, senderId)?.name || senderName);
    // const participantName =useBoundSelector(getParticipantByVMPUserId, senderUserId);
    const setCheckbox = useCallback((index, state) => {
        let newCheckBoxStates;
        if (poll?.chooseMultipleOptions) {
            newCheckBoxStates = [...checkBoxStates];
            newCheckBoxStates[index] = state;
        }
        else {
            newCheckBoxStates = new Array(poll.answers.length).fill(false);
            newCheckBoxStates[index] = true;
        }
        setCheckBoxState(newCheckBoxStates);
        conference?.sendPollMessage({
            type: COMMAND_ANSWER_POLL,
            poll_id: pollId,
            answers: newCheckBoxStates
        });
        dispatch(registerVote(pollId, newCheckBoxStates, false));
        // sendAnalytics(createPollEvent('vote.checked'));
    }, [checkBoxStates]);
    const removeMyAnswer = () => {
        let newCheckBoxStates;
        newCheckBoxStates = new Array(poll.answers.length).fill(false);
        setCheckBoxState(newCheckBoxStates);
        conference?.sendPollMessage({
            type: COMMAND_ANSWER_POLL,
            poll_id: pollId,
            answers: newCheckBoxStates
        });
        dispatch(registerVote(pollId, newCheckBoxStates, true));
    };
    const dispatch = useDispatch();
    const submitAnswer = useCallback(() => {
        // conference?.sendPollMessage({
        //     type: COMMAND_ANSWER_POLL,
        //     poll_id: pollId,
        //     answers: checkBoxStates
        // }); 
        // dispatch(updatePoll(pollId, {showResults: true}))                      
        // dispatch(registerVote(pollId, checkBoxStates, true));   
        return false;
    }, [pollId, checkBoxStates, conference]);
    const sendPoll = useCallback(() => {
        if (canCreatePoll) {
            conference?.sendPollMessage({
                type: COMMAND_START_POLLS,
                poll_id: pollId
            });
        }
        else {
            dispatch(showNotification({
                descriptionKey: "notify.lockStartPollTitel",
                titleKey: t("notify.lockStartPoll"),
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    }, [conference, question, answers]);
    const skipAnswer = useCallback(() => {
        dispatch(registerVote(pollId, null, true));
        sendAnalytics(createPollEvent('vote.skipped'));
    }, [pollId]);
    const skipChangeVote = useCallback(() => {
        dispatch(setVoteChanging(pollId, false));
    }, [dispatch, pollId]);
    const { t } = useTranslation();
    const _answers = useMemo(() => {
        const voterSet = new Set();
        // Getting every voters ID that participates to the poll
        for (const answer of poll.answers) {
            if (answer.voters?.size) {
                for (const voterId of Array.from(answer.voters.keys())) {
                    voterSet.add(voterId);
                }
            }
        }
        const totalVoters = voterSet.size;
        return poll.answers?.map((answer) => {
            if (answer.voters?.size) {
                const percentage = totalVoters === 0 ? 0 : Math.round(answer.voters.size / totalVoters * 100);
                let voters;
                voters = Array.from(answer.voters, ([id, name]) => {
                    return { id, name };
                });
                return {
                    name: answer.name,
                    percentage,
                    voters,
                    voterCount: answer.voters.size
                };
            }
            else {
                return {
                    name: answer.name,
                    percentage: 0,
                    voters: [],
                    voterCount: answer.voters.size
                };
            }
        });
    }, [answers]);
    const redirectFormAddOption = () => {
        dispatch(updatePoll(pollId, { showResults: false, isAddOption: true }));
    };
    const cancelAddOption = () => {
        dispatch(updatePoll(pollId, { isAddOption: undefined }));
    };
    const addOption = () => {
        if (optionValue.trim()) {
            // conference?.sendMessage({                                                                                                                                                                                                                            
            //     type: COMMAND_ADD_OPTION_POLL,
            //     poll_id: pollId,
            //     new_answers: [...poll.answers, {name: optionValue, voters: []}]
            // });
            // setCheckBoxState(prev => ([...prev, false]))
            conference?.sendPollMessage({
                type: COMMAND_ADD_OPTION_POLL,
                poll_id: pollId,
                new_answers: [optionValue]
            });
            setCheckBoxState(prev => ([...prev, false]));
            setOptionValue("");
            setValidateMaxLength(false);
        }
        cancelAddOption();
    };
    const deletePoll = (pollId) => {
        conference?.sendPollMessage({
            type: COMMAND_DELETE_POLL,
            poll_id: pollId
        });
    };
    const editPollHandle = () => {
        const { pollId } = poll;
        dispatch(editPoll(pollId));
    };
    const endPoll = () => {
        conference?.sendPollMessage({
            type: COMMAND_END_POLL,
            poll_id: pollId
        });
    };
    useEffect(() => {
        const localId = getLocalParticipant(APP.store.getState())?.id;
        const _checkBoxStates = new Array(poll?.answers.length).fill(false);
        let voters = {};
        for (let i = 0; i < poll?.answers.length; i++) {
            if (poll?.answers[i]?.voters.get(`${localId}`)) {
                _checkBoxStates[i] = true;
            }
            else
                _checkBoxStates[i] = false;
        }
        setCheckBoxState(_checkBoxStates);
    }, [poll.answers]);
    return (React.createElement(Component, { checkBoxStates: checkBoxStates, creatorName: participantName, poll: poll, pollId: pollId, sendPoll: sendPoll, setCheckbox: setCheckbox, setCreateMode: setCreateMode, skipAnswer: skipAnswer, skipChangeVote: skipChangeVote, submitAnswer: submitAnswer, _answers: _answers, redirectFormAddOption: redirectFormAddOption, optionValue: optionValue, setOptionValue: setOptionValue, addOption: addOption, cancelAddOption: cancelAddOption, deletePoll: deletePoll, editPollHandle: editPollHandle, endPoll: endPoll, removeMyAnswer: removeMyAnswer, disabled: disabled, validateMaxLength: validateMaxLength, setValidateMaxLength: setValidateMaxLength, t: t }));
};
export default AbstractPollAnswer;
